import { useState, useCallback } from 'react'
import {
  type Difficulty, type SudokuMode,
  GRID_SIZE, BOX_SIZE,
  generatePuzzle, computeConflicts, checkComplete, countCorrect, countEmpty,
} from './sudokuData'

export type { Difficulty, SudokuMode }

export interface SudokuState {
  mode:         SudokuMode
  size:         number       // 4 or 9
  boxSize:      number       // 2 or 3
  grid:         number[][]   // 0 = empty
  solution:     number[][]
  given:        boolean[][]
  selected:     [number, number] | null
  conflicts:    boolean[][]
  notes:        boolean[][]  // notes[r * size + c][n-1] stored flat for serializability
  notesMode:    boolean
  correct:      number       // correctly filled non-given cells
  totalEmpty:   number       // total non-given cells
  moves:        number       // valid number placements
  errors:       number       // wrong placements made
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

// ── Pure helpers ───────────────────────────────────────────────

function makeNotes(size: number): boolean[][] {
  return Array.from({ length: size * size }, () => Array(size).fill(false))
}

function initState(mode: SudokuMode, difficulty: Difficulty): SudokuState {
  const size    = GRID_SIZE[mode]
  const boxSize = BOX_SIZE[mode]
  const puzzle  = generatePuzzle(mode, difficulty)

  return {
    mode,
    size,
    boxSize,
    grid:         puzzle.grid,
    solution:     puzzle.solution,
    given:        puzzle.given,
    selected:     null,
    conflicts:    Array.from({ length: size }, () => Array(size).fill(false)),
    notes:        makeNotes(size),
    notesMode:    false,
    correct:      0,
    totalEmpty:   countEmpty(puzzle.given, size),
    moves:        0,
    errors:       0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

function applySelectCell(
  prev: SudokuState,
  r: number, c: number,
): SudokuState {
  // Deselect if tapping the same cell
  if (prev.selected && prev.selected[0] === r && prev.selected[1] === c) {
    return { ...prev, selected: null }
  }
  return { ...prev, selected: [r, c] }
}

function applyEnterNumber(prev: SudokuState, n: number): SudokuState {
  if (!prev.selected) return prev
  const [r, c] = prev.selected
  if (prev.given[r][c]) return prev

  const startTime = prev.startTime ?? Date.now()

  // Notes mode: toggle the note for this number
  if (prev.notesMode) {
    const idx  = r * prev.size + c
    const newNotes = prev.notes.map(cell => [...cell])
    newNotes[idx][n - 1] = !newNotes[idx][n - 1]
    return { ...prev, notes: newNotes, startTime }
  }

  // Same value already placed — no-op
  if (prev.grid[r][c] === n) return { ...prev, startTime }

  const newGrid = prev.grid.map(row => [...row])
  newGrid[r][c] = n

  // Clear notes for this cell
  const newNotes = prev.notes.map(cell => [...cell])
  newNotes[r * prev.size + c] = Array(prev.size).fill(false)

  // Auto-eliminate notes in peer cells when placement is correct
  if (n === prev.solution[r][c]) {
    const boxStartR = Math.floor(r / prev.boxSize) * prev.boxSize
    const boxStartC = Math.floor(c / prev.boxSize) * prev.boxSize

    // Same row
    for (let c2 = 0; c2 < prev.size; c2++) {
      if (c2 !== c) {
        newNotes[r * prev.size + c2][n - 1] = false
      }
    }
    // Same column
    for (let r2 = 0; r2 < prev.size; r2++) {
      if (r2 !== r) {
        newNotes[r2 * prev.size + c][n - 1] = false
      }
    }
    // Same box
    for (let dr = 0; dr < prev.boxSize; dr++) {
      for (let dc = 0; dc < prev.boxSize; dc++) {
        const r2 = boxStartR + dr
        const c2 = boxStartC + dc
        if (r2 !== r || c2 !== c) {
          newNotes[r2 * prev.size + c2][n - 1] = false
        }
      }
    }
  }

  const isWrong  = n !== prev.solution[r][c]
  const newMoves  = prev.moves + 1
  const newErrors = prev.errors + (isWrong ? 1 : 0)

  const conflicts    = computeConflicts(newGrid, prev.solution, prev.given, prev.size)
  const gameComplete = checkComplete(newGrid, prev.solution, prev.given, prev.size)
  const correct      = countCorrect(newGrid, prev.solution, prev.given, prev.size)

  return {
    ...prev,
    grid:         newGrid,
    notes:        newNotes,
    conflicts,
    correct,
    moves:        newMoves,
    errors:       newErrors,
    startTime,
    gameComplete,
    endTime:      gameComplete ? Date.now() : prev.endTime,
  }
}

function applyClearCell(prev: SudokuState): SudokuState {
  if (!prev.selected) return prev
  const [r, c] = prev.selected
  if (prev.given[r][c]) return prev

  // Nothing to clear
  if (prev.grid[r][c] === 0 && !prev.notes[r * prev.size + c].some(Boolean)) return prev

  const newGrid = prev.grid.map(row => [...row])
  newGrid[r][c] = 0

  const newNotes = prev.notes.map(cell => [...cell])
  newNotes[r * prev.size + c] = Array(prev.size).fill(false)

  const conflicts = computeConflicts(newGrid, prev.solution, prev.given, prev.size)
  const correct   = countCorrect(newGrid, prev.solution, prev.given, prev.size)

  return { ...prev, grid: newGrid, notes: newNotes, conflicts, correct }
}

// ── Hook ───────────────────────────────────────────────────────

export function useSudoku(mode: SudokuMode, difficulty: Difficulty) {
  const [state, setState] = useState<SudokuState>(() => initState(mode, difficulty))

  const resetGame = useCallback(() => {
    setState(initState(mode, difficulty))
  }, [mode, difficulty])

  const selectCell = useCallback((r: number, c: number) => {
    setState(prev => applySelectCell(prev, r, c))
  }, [])

  const enterNumber = useCallback((n: number) => {
    setState(prev => applyEnterNumber(prev, n))
  }, [])

  const clearCell = useCallback(() => {
    setState(prev => applyClearCell(prev))
  }, [])

  const toggleNotesMode = useCallback(() => {
    setState(prev => ({ ...prev, notesMode: !prev.notesMode }))
  }, [])

  return { state, resetGame, selectCell, enterNumber, clearCell, toggleNotesMode }
}
